import React, { useEffect, useState } from "react";
import {
  Button,
  makeStyles,
  TextField,
  Typography,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import Progress from '@configuratorware/configurator-frontendgui/App/Shared/Components/Progress/Progress';
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import CustomDialog from "@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog";

export const useStyles = makeStyles((theme) => ({
  loginDialog: {
    maxWidth: 450,
    margin: "auto",
  },
  infoText: {
    marginBottom: 20,
  },
  textField: {
    margin: "10px 0px",
    width: "100%",
    "& input": {
      padding: "10.5px 14px",
    },
  },
  activeButton: {
    width: "100%",
    padding: 8,
    textTransform: "none",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
}), {name: 'Login'});

export interface LoginProps {
  triggerLogin: (username: string, password: string) => void;
  show: boolean;
  onClose: ()=> void;
  loginError: boolean;
  isValidLogin: boolean;
  token: string | undefined;
  setLoginError: (flag: boolean) => void
}

const Login = ( props: LoginProps) => {
  const classes = useStyles();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const { triggerLogin, show, onClose, loginError, token, isValidLogin, setLoginError } = props;

  useEffect(() => {
    // disable login error when form is empty/reloaded 
    loginError && username === "" && password === "" && setLoginError(false);
  }, []);

  useEffect(() => {
    isValidLogin && resetData();
  }, [isValidLogin]);

  useEffect(() => {
    setHasError(loginError);
    loginError && setLoading(false);
  }, [loginError]);

  const resetData = () => {
    setUsername("");
    setPassword("");
    setLoading(false)
  }

  const onLogin = () => {
    loginError && setLoginError(false); // resetting error on retriggering login
    triggerLogin(username, password);
    setLoading(true);
    setHasError(false);
  }

  return (
    <CustomDialog
      open={show}
      className={classes.loginDialog}
      onCancel={() => {
        onClose();
        setLoginError(false);
        setUsername("");
        setPassword("");
      }}
      dialogTitle={t("loginDialog.title")}
    >
      <DialogContent>
        <Progress loading={loading && (!token && !hasError)} />
        <Typography classes={{ root: classes.infoText }} variant="body1">
          {t("loginDialog.info")}
        </Typography>
        <TextField
          autoFocus={true}
          className={classes.textField}
          variant="outlined"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t("loginDialog.username")}
        ></TextField>
        <TextField
          variant="outlined"
          className={classes.textField}
          value={password}
          type="password"
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && onLogin()}
          placeholder={t("loginDialog.password")}
        ></TextField>
        {loginError && (
          <Typography variant="body2" color="error">
            {t("loginDialog.error")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onLogin()}
          variant="contained"
          disabled={username.trim() === "" || password.trim() === ""}
          classes={{ root: classes.activeButton }}
        >
          {t("loginDialog.buttonLabel")}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default Login;
